import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FileSpecificationSettings } from 'src/app/models/file-specifications/file-specification-settings';
import { StillsDimensions } from 'src/app/models/file-specifications/stills-dimensions';
import { StillsFormatSettings } from 'src/app/models/file-specifications/stills-format-settings';
import { WebPCompression } from 'src/app/models/file-specifications/webp-compression';
import { OutputVideoFormat } from 'src/app/models/file-specifications/output-video-format';
import { VideoBitRate } from 'src/app/models/file-specifications/video-bit-rate';

const STILLS_FORMAT_LABELS: Record<StillsFormatSettings['kind'], string> = {
  jpg: 'JPG',
  png: 'PNG',
  tiff: 'TIFF',
  webp: 'WebP',
  eps: 'EPS',
};

const VIDEO_FORMAT_LABELS: Record<OutputVideoFormat, string> = {
  Mp4H264: 'MP4 H.264',
  Mp4H265: 'MP4 H.265',
  MovH264: 'MOV H.264',
  MovH265: 'MOV H.265',
  WebM: 'WebM',
  Gif: 'GIF',
};

export const stillsFormatLabel = (format: StillsFormatSettings): string =>
  STILLS_FORMAT_LABELS[format.kind];

export const videoFormatLabel = (format: OutputVideoFormat): string =>
  VIDEO_FORMAT_LABELS[format];

export const stillsDpiLabel = (dpi: number): string => (dpi === 0 ? 'Auto' : String(dpi));

export const stillsSizeLabel = (dimensions: StillsDimensions): string => {
  switch (dimensions.mode) {
    case 'fitWidth':
      return `${dimensions.width} × auto`;
    case 'fitHeight':
      return `auto × ${dimensions.height}`;
    case 'explicit':
      return `${dimensions.width} × ${dimensions.height}`;
    case 'longEdgeCap':
      return `long: ${dimensions.longEdge}`;
    case 'longEdgeBox':
      return `long: ${dimensions.longEdge} × short: ${dimensions.shortEdge}`;
  }
};

const jpgQualityBand = (quality: number): string => {
  if (quality >= 100) return 'Best';
  if (quality >= 80) return `High (${quality})`;
  if (quality >= 50) return `Medium (${quality})`;
  return `Low (${quality})`;
};

const webpQualityLabel = (compression: WebPCompression): string =>
  compression.mode === 'lossless' ? 'Lossless' : `Lossy (${compression.quality})`;

export const stillsQualityLabel = (format: StillsFormatSettings): string | null => {
  switch (format.kind) {
    case 'jpg':
      return jpgQualityBand(format.quality);
    case 'png':
    case 'tiff':
      return `${format.bitDepth}-bit`;
    case 'webp':
      return webpQualityLabel(format.compression);
    case 'eps':
      return null;
  }
};

const COLOUR_SPACE_LABELS: Record<'sRGB' | 'CMYK', string> = {
  sRGB: 'sRGB',
  CMYK: 'CMYK',
};

export const stillsProfileLabel = (format: StillsFormatSettings): string =>
  COLOUR_SPACE_LABELS[format.colorSpace];

export const videoSizeLabel = (width: number, height: number): string =>
  `${width} × ${height}`;

export const videoBitRateLabel = (bitRate: VideoBitRate): string =>
  bitRate.mode === 'auto' ? 'Auto' : `${bitRate.mbps} Mbps`;

export const specMediaLabel = (settings: FileSpecificationSettings): string =>
  settings.media === 'stills' ? 'Stills' : 'Videos';

export const specMediaIcon = (settings: FileSpecificationSettings): string =>
  settings.media === 'stills' ? 'image' : 'movie';

export interface SpecMetaBadge {
  icon: string;
  label: string;
}

export const specMetaBadge = (spec: FileSpecification): SpecMetaBadge => {
  const settings = spec.value.settings;
  if (settings.media === 'stills') {
    return { icon: 'image', label: `Stills · ${stillsFormatLabel(settings.encoding.format)}` };
  }
  return { icon: 'movie', label: `Videos · ${videoFormatLabel(settings.format)}` };
};
